import {
  CandlestickData,
  LiquidationIndicatorData,
  MacdIndicatorData,
  MarketStructureIndicatorData,
  MasterPatternIndicatorData,
  MaIndicatorData,
  VwapIndicatorData,
  OrderBlocksIndicatorData,
  RsiIndicatorData,
  ZigZagIndicatorData
} from '@server/data-feed/schemas/output.schema';
import { convertToTime } from '../utils/time-conversion.utils';
import { CandlestickUpdatePoint } from './candlestick-update.service';
import {
  MacdIndicatorUpdatePoint,
  LiquidationIndicatorUpdatePoint,
  MarketStructureIndicatorUpdatePoint,
  MasterPatternIndicatorUpdatePoint,
  MaIndicatorUpdatePoint,
  VwapIndicatorUpdatePoint,
  OrderBlocksIndicatorUpdatePoint,
  RsiIndicatorUpdatePoint,
  ZigZagIndicatorUpdatePoint
} from './indicator-update.service';

export const toCandlestickUpdatePoints = (
  data: CandlestickData[]
): CandlestickUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    open: item.open,
    high: item.high,
    low: item.low,
    close: item.close,
    volume: item.volume
  }));

export const toMaUpdatePoints = (
  data: MaIndicatorData[]
): MaIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    value: item.value
  }));

export const toVwapUpdatePoints = (
  data: VwapIndicatorData[]
): VwapIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    value: item.value
  }));

export const toRsiUpdatePoints = (
  data: RsiIndicatorData[]
): RsiIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    value: item.value,
    ma: item.ma
  }));

export const toMacdUpdatePoints = (
  data: MacdIndicatorData[]
): MacdIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    macd: item.macd,
    signal: item.signal,
    histogram: item.histogram
  }));

export const toLiquidationUpdatePoints = (
  data: LiquidationIndicatorData[]
): LiquidationIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    bullish: item.bullish,
    bearish: item.bearish,
    bullishSweep: item.bullishSweep
      ? {
          pivotTime: convertToTime(item.bullishSweep.pivotTime) as string,
          pivotPrice: item.bullishSweep.pivotPrice,
          breakTime: convertToTime(item.bullishSweep.breakTime) as string,
          breakPrice: item.bullishSweep.breakPrice,
          reclaimTime: convertToTime(item.bullishSweep.reclaimTime) as string,
          reclaimPrice: item.bullishSweep.reclaimPrice,
          confirmationTime: convertToTime(
            item.bullishSweep.confirmationTime
          ) as string,
          confirmationPrice: item.bullishSweep.confirmationPrice
        }
      : undefined,
    bearishSweep: item.bearishSweep
      ? {
          pivotTime: convertToTime(item.bearishSweep.pivotTime) as string,
          pivotPrice: item.bearishSweep.pivotPrice,
          breakTime: convertToTime(item.bearishSweep.breakTime) as string,
          breakPrice: item.bearishSweep.breakPrice,
          reclaimTime: convertToTime(item.bearishSweep.reclaimTime) as string,
          reclaimPrice: item.bearishSweep.reclaimPrice,
          confirmationTime: convertToTime(
            item.bearishSweep.confirmationTime
          ) as string,
          confirmationPrice: item.bearishSweep.confirmationPrice
        }
      : undefined,
    bullishLiquidation: item.bullishLiquidation
      ? {
          pivotTime: convertToTime(item.bullishLiquidation.pivotTime) as string,
          pivotPrice: item.bullishLiquidation.pivotPrice,
          breakTime: convertToTime(item.bullishLiquidation.breakTime) as string,
          breakPrice: item.bullishLiquidation.breakPrice,
          reclaimTime: convertToTime(
            item.bullishLiquidation.reclaimTime
          ) as string,
          reclaimPrice: item.bullishLiquidation.reclaimPrice,
          confirmationTime: convertToTime(
            item.bullishLiquidation.confirmationTime
          ) as string,
          confirmationPrice: item.bullishLiquidation.confirmationPrice
        }
      : undefined,
    bearishLiquidation: item.bearishLiquidation
      ? {
          pivotTime: convertToTime(item.bearishLiquidation.pivotTime) as string,
          pivotPrice: item.bearishLiquidation.pivotPrice,
          breakTime: convertToTime(item.bearishLiquidation.breakTime) as string,
          breakPrice: item.bearishLiquidation.breakPrice,
          reclaimTime: convertToTime(
            item.bearishLiquidation.reclaimTime
          ) as string,
          reclaimPrice: item.bearishLiquidation.reclaimPrice,
          confirmationTime: convertToTime(
            item.bearishLiquidation.confirmationTime
          ) as string,
          confirmationPrice: item.bearishLiquidation.confirmationPrice
        }
      : undefined,
    zigZag: item.zigZag
      ? {
          time: convertToTime(item.zigZag.time),
          isHigh: item.zigZag.isHigh,
          price: item.zigZag.price,
          swingStart: item.zigZag.swingStart
            ? {
                time:
                  item.zigZag.swingStart.time != null
                    ? convertToTime(item.zigZag.swingStart.time)
                    : undefined,
                price: item.zigZag.swingStart.price
              }
            : undefined,
          swingEnd: item.zigZag.swingEnd
            ? {
                time:
                  item.zigZag.swingEnd.time != null
                    ? convertToTime(item.zigZag.swingEnd.time)
                    : undefined,
                price: item.zigZag.swingEnd.price
              }
            : undefined
        }
      : undefined
  }));

export const toZigZagUpdatePoints = (
  data: ZigZagIndicatorData[]
): ZigZagIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    isHigh: item.isHigh,
    price: item.price,
    swingStart: item.swingStart
      ? {
          time:
            item.swingStart.time != null
              ? convertToTime(item.swingStart.time)
              : undefined,
          price: item.swingStart.price
        }
      : undefined,
    swingEnd: item.swingEnd
      ? {
          time:
            item.swingEnd.time != null
              ? convertToTime(item.swingEnd.time)
              : undefined,
          price: item.swingEnd.price
        }
      : undefined
  }));

export const toMasterPatternUpdatePoints = (
  data: MasterPatternIndicatorData[]
): MasterPatternIndicatorUpdatePoint[] =>
  data.map((item) => ({
    contractions: item.contractions?.map((contraction) => ({
      ...contraction,
      contraction_box_start: contraction.contraction_box_start
        ? (convertToTime(contraction.contraction_box_start) as string)
        : undefined,
      contraction_box_end: contraction.contraction_box_end
        ? (convertToTime(contraction.contraction_box_end) as string)
        : undefined,
      value_point_continuation_start: contraction.value_point_continuation_start
        ? (convertToTime(contraction.value_point_continuation_start) as string)
        : undefined,
      value_point_expired_date: contraction.value_point_expired_date
        ? (convertToTime(contraction.value_point_expired_date) as string)
        : undefined
    })),
    zigZag: item.zigZag?.map((zigZagItem) => ({
      ...zigZagItem,
      time: convertToTime(zigZagItem.time) as string,
      swingStart: zigZagItem.swingStart
        ? {
            ...zigZagItem.swingStart,
            time: zigZagItem.swingStart.time
              ? (convertToTime(zigZagItem.swingStart.time) as string)
              : undefined
          }
        : undefined,
      swingEnd: zigZagItem.swingEnd
        ? {
            ...zigZagItem.swingEnd,
            time: zigZagItem.swingEnd.time
              ? (convertToTime(zigZagItem.swingEnd.time) as string)
              : undefined
          }
        : undefined
    }))
  }));

export const toOrderBlocksUpdatePoints = (
  data: OrderBlocksIndicatorData[]
): OrderBlocksIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    orderBlockStart: item.orderBlockStart
      ? (convertToTime(item.orderBlockStart) as string)
      : undefined,
    orderBlockEnd: item.orderBlockEnd
      ? (convertToTime(item.orderBlockEnd) as string)
      : undefined,
    top: item.top,
    bottom: item.bottom,
    blockType: item.blockType
  }));

export const toMarketStructureUpdatePoints = (
  data: MarketStructureIndicatorData[]
): MarketStructureIndicatorUpdatePoint[] =>
  data.map((item) => ({
    time: convertToTime(item.time),
    type: item.type,
    direction: item.direction,
    price: item.price,
    lastPivotTime: item.lastPivotTime
      ? (convertToTime(item.lastPivotTime) as string)
      : undefined,
    lastPivotPrice: item.lastPivotPrice,
    breakTime: item.breakTime
      ? (convertToTime(item.breakTime) as string)
      : undefined,
    zigZag: item.zigZag
      ? {
          time: convertToTime(item.zigZag.time),
          isHigh: item.zigZag.isHigh,
          price: item.zigZag.price,
          swingStart: item.zigZag.swingStart
            ? {
                time:
                  item.zigZag.swingStart.time != null
                    ? convertToTime(item.zigZag.swingStart.time)
                    : undefined,
                price: item.zigZag.swingStart.price
              }
            : undefined,
          swingEnd: item.zigZag.swingEnd
            ? {
                time:
                  item.zigZag.swingEnd.time != null
                    ? convertToTime(item.zigZag.swingEnd.time)
                    : undefined,
                price: item.zigZag.swingEnd.price
              }
            : undefined
        }
      : undefined
  }));
